"use client";

import React, { useState } from "react";
import { Box } from "@mui/material";
import Link from "next/link";

import {
  ChevronDown,
  Facebook,
  ShieldAlert,
  MapPin,
  ExternalLink,
  BadgeDollarSign,
} from "lucide-react";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "/components/ui/card";

import WysiwygViewer from "components/TextEditorViewer";
import SectionHeading from "components/SectionHeading";
import useSiteSettings from "utils/useSiteSettings";

const ICON_TONES = {
  primary: "bg-primary text-white shadow-md shadow-primary/30",
  blue: "bg-brand-blue text-white shadow-md shadow-brand-blue/30",
  amber: "bg-amber-500 text-white shadow-md shadow-amber-500/30",
};

// Нээгддэг үйлчилгээний карт: толгойг дарахад агуулга нээгдэнэ/хаагдана
const ServiceCard = ({ icon: Icon, tone = "primary", title, hint, open, onToggle, children }) => (
  <Card
    className={`rounded-2xl bg-white transition-all duration-300 ${
      open
        ? "border-primary shadow-xl ring-4 ring-primary/10"
        : "border-border hover:border-primary/40 hover:shadow-lg"
    }`}
  >
    <CardHeader
      role="button"
      tabIndex={0}
      aria-expanded={open}
      onClick={onToggle}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onToggle();
        }
      }}
      className="flex flex-row items-center gap-4 space-y-0 p-5 cursor-pointer select-none"
    >
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${ICON_TONES[tone]}`}>
        <Icon className="h-6 w-6" />
      </div>
      <div className="flex-1 min-w-0">
        <CardTitle className="font-display text-base font-bold leading-snug text-foreground">{title}</CardTitle>
        {!open && <CardDescription className="mt-1 text-xs">{hint}</CardDescription>}
      </div>
      <span
        className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-all duration-300 ${
          open ? "rotate-180 bg-primary text-white" : "bg-muted text-muted-foreground"
        }`}
      >
        <ChevronDown className="w-4 h-4" />
      </span>
    </CardHeader>

    {open && <CardContent className="px-5 pb-5 pt-0">{children}</CardContent>}
  </Card>
);

export default function Services({
  location = [],
  linkData = [],
  connect = [],
  price = [],
}) {
  const { get } = useSiteSettings();
  const [activeSection, setActiveSection] = useState(null);
  const [copied, setCopied] = useState("");

  const toggleSection = (key) => {
    setActiveSection((prev) => (prev === key ? null : key));
  };

  const handleCopy = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopied(text);
    setTimeout(() => setCopied(""), 1600);
  };

  return (
    <section className="py-20 bg-slate-50">
      {copied && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[70] bg-foreground text-white text-sm font-medium px-4 py-2.5 rounded-lg shadow-lg animate-in fade-in slide-in-from-bottom-3">
          Хуулагдлаа: {copied}
        </div>
      )}
      <div className="container mx-auto px-4">
        <SectionHeading eyebrow="Үйлчилгээ" title={get("services.title")} subtitle={get("services.subtitle")} />

        {/* items-start: нэг карт нээгдэхэд хажуугийнх нь сунахгүй */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 items-start">
          {/* ========================= ХАЯГ ХОЛБОХ ========================= */}
          {connect.map((item, index) => {
            const sectionKey = `address-${index}`;

            return (
              <ServiceCard
                key={item.id || index}
                icon={MapPin}
                title={`Хаяг холбох — ${item.branch} салбар`}
                hint="Хаягийн дэлгэрэнгүй харахын тулд дарна уу."
                open={activeSection === sectionKey}
                onToggle={() => toggleSection(sectionKey)}
              >
                <Box sx={{ fontFamily: "Tahoma", fontSize: 14, lineHeight: 2 }}>
                  {[
                    {
                      label: "1. 收件人 (Хүлээн авагч):",
                      value: item.name,
                    },
                    { label: "2. 电话 (Утас):", value: item.phone },
                    {
                      label: "3. 所在地区 (Бүс нутаг):",
                      value: item.price,
                    },
                    { label: "4. Хаяг:", value: item.location },
                  ].map((row, i) => (
                    <Box
                      key={i}
                      sx={{
                        mt: 2,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        borderBottom: i < 3 ? "1px solid #e5e7eb" : "none",
                        pb: 1,
                      }}
                    >
                      <span className="font-medium text-gray-700">
                        {row.label}
                      </span>

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <span className="text-gray-800">{row.value}</span>

                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCopy(row.value || "");
                          }}
                          className="px-2 py-1 rounded-md bg-primary text-white text-xs hover:bg-primary-hover transition"
                        >
                          Хуулах
                        </button>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </ServiceCard>
            );
          })}

          {/* ========================= ҮНИЙН МЭДЭЭЛЭЛ ========================= */}
          {price.map((service, index) => {
            const sectionKey = `price-${index}`;

            return (
              <ServiceCard
                key={index}
                icon={BadgeDollarSign}
                title="Үнэ / Тариф"
                hint="Үнийн мэдээллийг дэлгэрэнгүй харах."
                open={activeSection === sectionKey}
                onToggle={() => toggleSection(sectionKey)}
              >
                <Box className="text-gray-700 leading-relaxed">
                  <WysiwygViewer data={service.content} />
                </Box>
              </ServiceCard>
            );
          })}

          {/* ========================= ЛИНК ЗААВАР ========================= */}
          <ServiceCard
            icon={ExternalLink}
            tone="blue"
            title="Заавар линкүүд"
            hint="Бүх зааврын линкүүдийг харахын тулд дарна уу."
            open={activeSection === "links"}
            onToggle={() => toggleSection("links")}
          >
            <div className="space-y-2">
              {linkData.map((row, idx) => (
                <Link
                  href={row.link}
                  key={idx}
                  target="_blank"
                  className="flex items-center justify-between gap-3 rounded-xl border border-border bg-slate-50 px-4 py-3 text-sm font-medium text-foreground hover:border-brand-blue/40 hover:bg-brand-blue/5 transition-colors"
                >
                  {row.description}
                  <ExternalLink className="w-4 h-4 text-brand-blue shrink-0" />
                </Link>
              ))}
            </div>
          </ServiceCard>

          {/* ========================= АНХААРАХ ЗҮЙЛС ========================= */}
          <ServiceCard
            icon={ShieldAlert}
            tone="amber"
            title="Анхаарах зүйлс"
            hint="Анхаарах зүйлсийг харахын тулд дарна уу."
            open={activeSection === "warning"}
            onToggle={() => toggleSection("warning")}
          >
            <Box className="text-gray-700 leading-relaxed whitespace-pre-line">
              {get("services.warning")}
            </Box>
          </ServiceCard>

          {/* ========================= FACEBOOK GROUP ========================= */}
          {get("services.facebookUrl") && (
            <ServiceCard
              icon={Facebook}
              tone="blue"
              title="Facebook хуудас / Group"
              hint="Facebook хуудас / group харахын тулд дарна уу."
              open={activeSection === "facebook"}
              onToggle={() => toggleSection("facebook")}
            >
              <Link
                href={get("services.facebookUrl")}
                target="_blank"
                className="flex items-center justify-between gap-3 rounded-xl border border-border bg-brand-blue/5 px-4 py-3 text-sm font-semibold text-brand-blue hover:border-brand-blue/40 transition-colors"
              >
                {get("services.facebookLabel")}
                <ExternalLink className="w-4 h-4 shrink-0" />
              </Link>
            </ServiceCard>
          )}
        </div>
      </div>
    </section>
  );
}
